/**
 * Twins Store
 *
 * Zustand store for Twins cluster proposals (active and reviewed). The agent's
 * settings live in agent_settings through the shared agent settings store (DEP-037).
 * DB-first: all writes go to Supabase before local state updates.
 */

import { create } from 'zustand';
import {
  getActiveClusters,
  getClusterHistory,
  acceptCluster,
  dismissCluster,
  type TwinsCluster,
} from '../../lib/twinsAgentService';

// ── State interface ───────────────────────────────────────────────────────────

export interface TwinsStoreState {
  activeClusters: TwinsCluster[];
  history: TwinsCluster[];
  clustersLoading: boolean;
  error: string | null;

  /** Fetch active clusters for the workspace. */
  fetchClusters: (workspaceId: string) => Promise<void>;

  /** Fetch accepted/dismissed cluster history. */
  fetchHistory: (workspaceId: string, limit?: number) => Promise<void>;

  /** Accept a cluster (move from active → accepted). */
  accept: (clusterId: string, userId: string) => Promise<{ success: boolean; error?: string; relationshipGroupId?: string | null }>;

  /** Dismiss a cluster (move from active → dismissed). */
  dismiss: (clusterId: string, userId: string) => Promise<{ success: boolean; error?: string }>;
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useTwinsStore = create<TwinsStoreState>((set) => ({
  activeClusters: [],
  history: [],
  clustersLoading: false,
  error: null,

  fetchClusters: async (workspaceId) => {
    set({ clustersLoading: true });
    const { data, error } = await getActiveClusters(workspaceId);
    if (error) {
      set({ clustersLoading: false, error });
      return;
    }
    set({ activeClusters: data, clustersLoading: false });
  },

  fetchHistory: async (workspaceId, limit = 50) => {
    const { data, error } = await getClusterHistory(workspaceId, limit);
    if (!error) {
      set({ history: data });
    }
  },

  accept: async (clusterId, userId) => {
    const { data, error } = await acceptCluster(clusterId, userId);
    if (error || !data) {
      // Store it so the page's existing error banner picks it up; the
      // cluster stays in activeClusters, so nothing else needs to revert.
      const message = error ?? 'Accept failed';
      set({ error: message });
      return { success: false, error: message };
    }

    // Move cluster from active to history
    set((state) => ({
      activeClusters: state.activeClusters.filter((c) => c.id !== clusterId),
      history: [data, ...state.history],
      error: null,
    }));
    return { success: true, relationshipGroupId: data.relationshipGroupId ?? null };
  },

  dismiss: async (clusterId, userId) => {
    const { data, error } = await dismissCluster(clusterId, userId);
    if (error || !data) {
      const message = error ?? 'Dismiss failed';
      set({ error: message });
      return { success: false, error: message };
    }

    set((state) => ({
      activeClusters: state.activeClusters.filter((c) => c.id !== clusterId),
      history: [data, ...state.history],
      error: null,
    }));
    return { success: true };
  },
}));
